import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const templates = [
  {
    id: 1,
    name: 'eCommerce Store',
    category: 'Business',
    description: 'Complete online store with product catalog, cart, and checkout',
    image: 'https://public.readdy.ai/ai/img_res/36af14f653745e0abb965ce5aa4fa5ae.jpg',
    icon: 'ri-shopping-bag-line',
    color: 'from-purple-500 to-pink-500',
  },
  {
    id: 2,
    name: 'Restaurant Menu',
    category: 'Food & Drink',
    description: 'Digital menu with ordering, reservations, and delivery tracking',
    image: 'https://readdy.ai/api/search-image?query=elegant%20restaurant%20mobile%20app%20interface%20showcasing%20beautiful%20food%20menu%20design%20with%20appetizing%20dish%20photos%20category%20tabs%20order%20button%20smooth%20animations%20warm%20orange%20red%20color%20palette%20professional%20culinary%20app%20ui%20modern%20minimalist%20layout%20perfect%20for%20food%20ordering%20application&width=400&height=600&seq=template-restaurant-app-v2-002&orientation=portrait',
    icon: 'ri-restaurant-line',
    color: 'from-orange-500 to-red-500',
  },
  {
    id: 3,
    name: 'Fitness Tracker',
    category: 'Health & Fitness',
    description: 'Workout plans, progress tracking, and nutrition logging',
    image: 'https://readdy.ai/api/search-image?query=dynamic%20fitness%20mobile%20app%20interface%20featuring%20workout%20tracking%20dashboard%20with%20exercise%20cards%20progress%20charts%20activity%20stats%20vibrant%20green%20blue%20gradient%20color%20scheme%20energetic%20design%20modern%20athletic%20ui%20perfect%20for%20health%20fitness%20application&width=400&height=600&seq=template-fitness-app-v2-003&orientation=portrait',
    icon: 'ri-heart-pulse-line',
    color: 'from-green-500 to-teal-500',
  },
  {
    id: 4,
    name: 'Real Estate',
    category: 'Business',
    description: 'Property listings with search, filters, and virtual tours',
    image: 'https://readdy.ai/api/search-image?query=sophisticated%20real%20estate%20mobile%20app%20interface%20displaying%20property%20listings%20with%20beautiful%20home%20photos%20search%20filters%20map%20view%20elegant%20blue%20gray%20color%20scheme%20professional%20property%20app%20ui%20clean%20modern%20design%20perfect%20for%20real%20estate%20application&width=400&height=600&seq=template-realestate-app-v2-004&orientation=portrait',
    icon: 'ri-home-line',
    color: 'from-blue-500 to-indigo-500',
  },
  {
    id: 5,
    name: 'Portfolio Showcase',
    category: 'Creative',
    description: 'Stunning portfolio to display your work and projects',
    image: 'https://readdy.ai/api/search-image?query=creative%20portfolio%20mobile%20app%20interface%20featuring%20elegant%20project%20gallery%20masonry%20layout%20beautiful%20artwork%20displays%20smooth%20transitions%20purple%20pink%20gradient%20accents%20modern%20artistic%20ui%20design%20perfect%20for%20creative%20professional%20portfolio%20application&width=400&height=600&seq=template-portfolio-app-v2-005&orientation=portrait',
    icon: 'ri-briefcase-line',
    color: 'from-pink-500 to-purple-500',
  },
  {
    id: 6,
    name: 'Booking System',
    category: 'Services',
    description: 'Appointment scheduling with calendar and notifications',
    image: 'https://readdy.ai/api/search-image?query=professional%20booking%20mobile%20app%20interface%20showing%20appointment%20calendar%20time%20slots%20service%20selection%20clean%20scheduling%20ui%20teal%20cyan%20color%20scheme%20modern%20minimalist%20design%20perfect%20for%20appointment%20booking%20service%20application&width=400&height=600&seq=template-booking-app-v2-006&orientation=portrait',
    icon: 'ri-calendar-check-line',
    color: 'from-teal-500 to-cyan-500',
  },
];

export default function TemplatesGallery() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('templates-gallery');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return (
    <section id="templates-gallery" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className={`inline-flex items-center gap-2 px-5 py-2.5 bg-purple-100 rounded-full text-purple-700 mb-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <i className="ri-layout-grid-line"></i>
            <span className="text-sm font-semibold">Ready to Use</span>
          </div>
          <h2 className={`text-5xl font-extrabold text-gray-900 mb-6 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            App Templates Gallery
          </h2>
          <p className={`text-xl text-gray-600 max-w-2xl mx-auto transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            Choose from professionally designed templates and customize them to match your brand
          </p>
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {templates.map((template, index) => (
            <div
              key={template.id}
              className={`group bg-white rounded-2xl overflow-hidden border-2 border-gray-100 hover:border-purple-300 hover:shadow-2xl transition-all duration-500 cursor-pointer ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
              style={{ transitionDelay: `${300 + index * 100}ms` }}
            >
              {/* Template Image */}
              <div className="relative h-80 overflow-hidden bg-gray-100">
                <img
                  src={template.image}
                  alt={template.name}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Category Badge */}
                <div className="absolute top-4 left-4 px-4 py-2 bg-white/95 backdrop-blur-sm rounded-lg text-xs font-bold text-gray-900">
                  {template.category}
                </div>

                {/* Icon */}
                <div className={`absolute top-4 right-4 w-12 h-12 bg-gradient-to-br ${template.color} rounded-xl flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:scale-110`}>
                  <i className={`${template.icon} text-2xl text-white`}></i>
                </div>

                {/* Hover Actions */}
                <div className="absolute bottom-4 left-4 right-4 flex gap-3 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-4 group-hover:translate-y-0">
                  <Link
                    to="/get-started"
                    className="flex-1 px-4 py-3 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer font-semibold text-sm text-center"
                  >
                    Use Template
                  </Link>
                  <button className="px-4 py-3 bg-white/20 backdrop-blur-md text-white rounded-lg hover:bg-white/30 transition-colors whitespace-nowrap cursor-pointer font-semibold text-sm border border-white/30">
                    <i className="ri-eye-line"></i>
                  </button>
                </div>
              </div>

              {/* Template Info */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors">
                  {template.name}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {template.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* View All CTA */}
        <div className={`text-center mt-16 transition-all duration-700 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Link
            to="/templates"
            className="inline-flex items-center gap-3 px-10 py-4 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer font-semibold"
          >
            View All Templates
            <i className="ri-arrow-right-line text-xl"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}